import React, { useState, useMemo, useContext } from 'react';
import {
  ChefHat,
  Coffee,
  Flame,
  Wheat,
  UtensilsCrossed,
  Soup,
  LayoutDashboard,
  Search,
  ArrowRight,
  Droplet,
  Fish,
  ShoppingBag,
  Menu,
  X,
  PieChart,
  Circle,
  Download
} from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import StoreSelector from '../StoreSelector';


/* DEPENDENCIES:
  - lucide-react (Icons)
  - bootstrap (CSS Framework) - Loaded via CDN in the component
*/


// --- DATA EXTRACTION FROM USER PDF ---
// This data represents the sales from 27-01-2026 based on the provided report.


const SALES_DATA = [
  // --- GRILLS & BBQ ---
  { id: 'g1', name: 'Shawwaya Half (2 KP)', category: 'Grills', qty: 17, price: 0, ingredients: { 'Chicken (Whole)': 0.5, 'Kerala Paratha': 2 } },
  { id: 'g2', name: 'Shawwaya Full (4 KP)', category: 'Grills', qty: 7, price: 0, ingredients: { 'Chicken (Whole)': 1, 'Kerala Paratha': 4 } },
  { id: 'g3', name: 'Shawwaya Qtr (1 Khubz)', category: 'Grills', qty: 8, price: 0, ingredients: { 'Chicken (Whole)': 0.25, 'Khubz': 1 } },
  { id: 'g4', name: 'Shawwaya Qtr (2 KP)', category: 'Grills', qty: 7, price: 0, ingredients: { 'Chicken (Whole)': 0.25, 'Kerala Paratha': 2 } },
  { id: 'g5', name: 'Al Faham Qtr (1 Khubz)', category: 'Grills', qty: 12, price: 0, ingredients: { 'Chicken (Whole)': 0.25, 'Khubz': 1 } },
  { id: 'g6', name: 'Al Faham Half (2 KP)', category: 'Grills', qty: 5, price: 0, ingredients: { 'Chicken (Whole)': 0.5, 'Kerala Paratha': 2 } },
  { id: 'g7', name: 'Al Faham Full (4 KP)', category: 'Grills', qty: 1, price: 0, ingredients: { 'Chicken (Whole)': 1, 'Kerala Paratha': 4 } },
  { id: 'g8', name: 'Tandoori Full (2 Wheat)', category: 'Grills', qty: 1, price: 0, ingredients: { 'Chicken (Whole)': 1, 'Wheat Paratha': 2 } },
  { id: 'g9', name: 'Tandoori Qtr (1 KP)', category: 'Grills', qty: 3, price: 0, ingredients: { 'Chicken (Whole)': 0.25, 'Kerala Paratha': 1 } },
  { id: 'g10', name: 'Peri Peri BBQ Qtr', category: 'Grills', qty: 1, price: 0, ingredients: { 'Chicken (Whole)': 0.25 } },
  { id: 'g11', name: 'Shawwaya Half (2 Khubz)', category: 'Grills', qty: 5, price: 0, ingredients: { 'Chicken (Whole)': 0.5, 'Khubz': 2 } },


  // --- BREADS (DOSA HUT) ---
  { id: 'b1', name: 'Kerala Paratha (Standalone)', category: 'Breads', qty: 149, price: 0, ingredients: { 'Kerala Paratha': 1 } },
  { id: 'b2', name: 'Appam', category: 'Breads', qty: 28, price: 0, ingredients: { 'Appam Batter (Ladle)': 1 } },
  { id: 'b3', name: 'Wheat Paratha', category: 'Breads', qty: 24, price: 0, ingredients: { 'Wheat Paratha': 1 } },
  { id: 'b4', name: 'Chapathi', category: 'Breads', qty: 22, price: 0, ingredients: { 'Chapathi': 1 } },
  { id: 'b5', name: 'Butter Naan', category: 'Breads', qty: 14, price: 0, ingredients: { 'Naan Dough': 1, 'Butter (tbsp)': 1 } },
  { id: 'b6', name: 'Thattu Dosa Plate', category: 'Breads', qty: 1, price: 0, ingredients: { 'Dosa Batter (Ladle)': 3 } },
  { id: 'b7', name: 'Masala Dosa', category: 'Breads', qty: 1, price: 0, ingredients: { 'Dosa Batter (Ladle)': 1, 'Potato Masala (g)': 150 } },
  { id: 'b8', name: 'Ghee Roast', category: 'Breads', qty: 4, price: 0, ingredients: { 'Dosa Batter (Ladle)': 1, 'Ghee (tbsp)': 1 } },
  { id: 'b9', name: 'Pathiri', category: 'Breads', qty: 11, price: 0, ingredients: { 'Pathiri': 1 } },


  // --- BEVERAGES ---
  { id: 'bev1', name: 'Lime Juice', category: 'Beverages', qty: 72, price: 0, ingredients: { 'Lime (pcs)': 1.5, 'Sugar (g)': 30 } },
  { id: 'bev2', name: 'Lime N Mint Cooler', category: 'Beverages', qty: 17, price: 0, ingredients: { 'Lime (pcs)': 1.5, 'Mint Leaves (sprig)': 1, 'Sugar (g)': 30 } },
  { id: 'bev3', name: 'Lemon Soda', category: 'Beverages', qty: 13, price: 0, ingredients: { 'Lime (pcs)': 1.5, 'Soda Water (ml)': 200 } },
  { id: 'bev4', name: 'Watermelon Juice', category: 'Beverages', qty: 9, price: 0, ingredients: { 'Watermelon (g)': 300 } },
  { id: 'bev5', name: 'Pineapple Juice', category: 'Beverages', qty: 14, price: 0, ingredients: { 'Pineapple (g)': 250 } },
  { id: 'bev6', name: 'Special Falooda', category: 'Beverages', qty: 9, price: 0, ingredients: { 'Milk (ml)': 200, 'Ice Cream (scoop)': 1, 'Vermicelli (g)': 50 } },
  { id: 'bev7', name: 'Tea (Black/Milk/Mint)', category: 'Beverages', qty: 43, price: 0, ingredients: { 'Tea Powder (g)': 5, 'Milk (ml)': 50, 'Sugar (g)': 10 } },


  // --- RICE & NOODLES ---
  { id: 'r1', name: 'Chicken Fried Rice', category: 'Rice/Noodles', qty: 14, price: 0, ingredients: { 'Rice (cooked g)': 250, 'Chicken Boneless (g)': 100, 'Egg': 1 } },
  { id: 'r2', name: 'Chicken Biriyani', category: 'Rice/Noodles', qty: 9, price: 0, ingredients: { 'Biriyani Rice (g)': 300, 'Chicken (pc)': 2 } },
  { id: 'r3', name: 'Sez Chicken Fried Rice', category: 'Rice/Noodles', qty: 4, price: 0, ingredients: { 'Rice (cooked g)': 250, 'Chicken Boneless (g)': 100, 'Schezwan Sauce (tbsp)': 2 } },
  { id: 'r4', name: 'Chicken Noodles', category: 'Rice/Noodles', qty: 4, price: 0, ingredients: { 'Noodles (g)': 200, 'Chicken Boneless (g)': 80 } },


  // --- SEAFOOD & MAINS ---
  { id: 's1', name: 'Seer Fish Tawa Fry', category: 'Seafood/Mains', qty: 2, price: 0, ingredients: { 'Seer Fish Slice': 1 } },
  { id: 's2', name: 'Prawns Tawa Fry', category: 'Seafood/Mains', qty: 1, price: 0, ingredients: { 'Prawns (g)': 200 } },
  { id: 's3', name: 'Beef Tenderloin Steak', category: 'Seafood/Mains', qty: 3, price: 0, ingredients: { 'Beef Steak (g)': 250 } },
  { id: 's4', name: 'Beef Roast', category: 'Seafood/Mains', qty: 2, price: 0, ingredients: { 'Beef Cubes (g)': 200, 'Onion Masala (g)': 150 } },

  // --- MANDHI ---
  { id: 'm1', name: 'Shawwaya Mandi Full', category: 'Mandhi', qty: 4, price: 0, ingredients: { 'Chicken (Whole)': 1, 'Mandi Rice (kg)': 1 } },
  { id: 'm2', name: 'Shawwaya Mandi Half', category: 'Mandhi', qty: 3, price: 0, ingredients: { 'Chicken (Whole)': 0.5, 'Mandi Rice (kg)': 0.5 } },
  { id: 'm3', name: 'Al Faham Mandi Full', category: 'Mandhi', qty: 2, price: 0, ingredients: { 'Chicken (Whole)': 1, 'Mandi Rice (kg)': 1 } },
];


// Bootstrap Color mapping
const CATEGORY_CONFIG = {
  'All': { bsClass: 'text-secondary bg-light', icon: <LayoutDashboard size={20} /> },
  'Grills': { bsClass: 'text-warning bg-warning-subtle', icon: <Flame size={20} /> },
  'Breads': { bsClass: 'text-secondary bg-secondary-subtle', icon: <Wheat size={20} /> },
  'Beverages': { bsClass: 'text-info bg-info-subtle', icon: <Coffee size={20} /> },
  'Rice/Noodles': { bsClass: 'text-success bg-success-subtle', icon: <UtensilsCrossed size={20} /> },
  'Seafood/Mains': { bsClass: 'text-danger bg-danger-subtle', icon: <Fish size={20} /> },
  'Mandhi': { bsClass: 'text-dark bg-warning', icon: <Soup size={20} /> },
};


const InventoryDashboard = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const {
    sidebar,
    setSidebar,
    Dark,
    setDark,
    selectedStoreID,
    selectedRestID,
    ApiConfig,
    storesList,
    permissions,
    setSelectedStoreID
  } = useContext(AppContext);

  // 1. Filter Data
  const filteredData = useMemo(() => {
    return SALES_DATA.filter(item => {
      const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchTerm]);


  // 2. Aggregate Raw Materials with Split (Individual vs Combo)
  const materialUsage = useMemo(() => {
    const usage = {};
    filteredData.forEach(item => {
      // Logic: If sold in 'Grills' or 'Mandhi', it's likely a Combo/Platter
      // If sold in 'Breads', 'Beverages', etc., it's an Individual order
      const isComboSource = ['Grills', 'Mandhi'].includes(item.category);

      Object.entries(item.ingredients).forEach(([ingredient, amountPerUnit]) => {
        const totalAmount = amountPerUnit * item.qty;

        if (!usage[ingredient]) {
          usage[ingredient] = { total: 0, individual: 0, combo: 0 };
        }

        usage[ingredient].total += totalAmount;

        if (isComboSource) {
          usage[ingredient].combo += totalAmount;
        } else {
          usage[ingredient].individual += totalAmount;
        }
      });
    });
    return Object.entries(usage).sort((a, b) => b[1].total - a[1].total);
  }, [filteredData]);


  // 3. Portion Analysis
  const portionStats = useMemo(() => {
    const stats = { Full: 0, Half: 0, Quarter: 0, Other: 0 };
    let hasPortions = false;


    filteredData.forEach(item => {
      const lowerName = item.name.toLowerCase();
      if (lowerName.includes('full')) {
        stats.Full += item.qty;
        hasPortions = true;
      } else if (lowerName.includes('half')) {
        stats.Half += item.qty;
        hasPortions = true;
      } else if (lowerName.includes('qtr') || lowerName.includes('quarter')) {
        stats.Quarter += item.qty;
        hasPortions = true;
      } else {
        stats.Other += item.qty;
      }
    });

    return hasPortions ? stats : null;
  }, [filteredData]);


  // 4. Totals
  const totalItemsSold = filteredData.reduce((acc, curr) => acc + curr.qty, 0);


  // 5. Handle Excel/CSV Export
  const handleExport = () => {
    // Define Headers
    const headers = ['Ingredient Name', 'Total Qty Used', 'Individual Orders', 'Combo/Platter Orders'];

    // Format Rows
    const rows = materialUsage.map(([name, stats]) => [
      `"${name}"`, // Quote strings to handle commas in names
      stats.total.toFixed(2),
      stats.individual.toFixed(2),
      stats.combo.toFixed(2)
    ]);


    // Construct CSV Content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');


    // Create Blob and Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Inventory_Report_${activeCategory}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  return (
    <>
      {/* Bootstrap CDN for Preview/Dev
        In production, run: npm install bootstrap
        and import 'bootstrap/dist/css/bootstrap.min.css' in index.js
      */}
      {/* <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css"
        rel="stylesheet"
        crossOrigin="anonymous"
      /> */}

      {/* Custom Styles */}
      <style>{`
        body { background-color: #f8f9fa; }
        .hover-bg-light:hover { background-color: #f8f9fa; transition: background-color 0.2s; }
        .cursor-pointer { cursor: pointer; }
        .gradient-card { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
        .glass-badge { background-color: rgba(255, 255, 255, 0.2); border: 1px solid rgba(255, 255, 255, 0.25); }
        .category-dot { width: 8px; height: 8px; display: inline-block; border-radius: 50%; }
        /* Smooth Sidebar Transition for mobile */
        @media (max-width: 767.98px) {
          .sidebar-mobile {
            transition: transform 0.3s ease-in-out;
            transform: translateX(-100%);
          }
          .sidebar-mobile.show {
            transform: translateX(0);
          }
        }
      `}</style>



      <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
        <div className="role-content-main">

          <div className="min-vh-100--">

            {/* HEADER */}
            <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom sticky-top shadow-sm z-3">
              <div className="container-fluid px-3 px-md-4">
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-light d-md-none border-0 p-2 me-1"
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  >
                    {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
                  </button>

                  <a className="navbar-brand d-flex align-items-center gap-2 fw-bold text-dark m-0" href="#">
                    <ChefHat size={28} className="text-primary" />
                    <span className="d-none d-sm-inline">ZAMZAM Grande</span>
                    <span className="d-sm-none">ZAMZAM</span>
                  </a>
                  <span style={badgeStyle} className="badge bg-light text-secondary border fw-normal ms-2 d-none d-md-inline-block">Sales Vs Material Used report</span>

                       
                </div>
                {permissions && permissions["View Sales VS Material Report"] && (
                                                <StoreSelector
                                                  options={Array.isArray(storesList) ? storesList.map(store => ({
                                                    value: store.value,
                                                    label: store.label,
                                                  })) : []}
                                                  value={selectedStoreID}
                                                  onChange={(value) => {
                                                    setSelectedStoreID(value);
                                                    localStorage.setItem("selectedStoreId", value);
                                                  }}
                                                />
                                              )}

                {/* <div className="d-flex ms-auto">
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <Search size={16} className="text-secondary" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 bg-light"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ maxWidth: '200px' }}
                />
              </div>
            </div> */}
              </div>
            </nav>


            <div className="container-fluid">
              <div className="row">

                {/* SIDEBAR OVERLAY (Mobile only) */}
                {isSidebarOpen && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-md-none"
                    style={{ zIndex: 1040 }}
                    onClick={() => setIsSidebarOpen(false)}
                  ></div>
                )}


                {/* SIDEBAR */}
                <nav className={`col-md-3 col-lg-3 bg-white sidebar border-end min-vh-100 p-0 ${isSidebarOpen ? 'position-fixed top-0 start-0 h-100 z-3 shadow sidebar-mobile show' : 'd-none d-md-block'}`} style={{ zIndex: 1050, width: isSidebarOpen ? '280px' : '' }}>
                  <div className="position-sticky pt-3 h-100 overflow-auto">
                    <div className="d-flex d-md-none align-items-center justify-content-between px-3 mb-3">
                      <span className="fw-bold text-uppercase text-muted small">Categories</span>
                      <button className="btn btn-sm btn-light" onClick={() => setIsSidebarOpen(false)}><X size={16} /></button>
                    </div>


                    <h6 className="sidebar-heading d-none d-md-flex justify-content-between align-items-center px-3 mt-4 mb-2 text-muted text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                      <span>Categories</span>
                    </h6>
                    <ul className="nav flex-column mb-auto">
                      {Object.keys(CATEGORY_CONFIG).map(cat => {
                        // Calculate count for this category
                        const count = cat === 'All'
                          ? SALES_DATA.reduce((a, b) => a + b.qty, 0)
                          : SALES_DATA.filter(i => i.category === cat).reduce((a, b) => a + b.qty, 0);


                        return (
                          <li className="nav-item" key={cat}>
                            <button
                              onClick={() => {
                                setActiveCategory(cat);
                                setIsSidebarOpen(false); // Close sidebar on selection (mobile)
                              }}
                              className={`nav-link w-100 d-flex align-items-center gap-2 text-start border-0 bg-transparent cursor-pointer px-3 py-2 my-1 mx-2 rounded-2 ${activeCategory === cat ? 'active fw-bold text-primary bg-primary-subtle' : 'text-secondary hover-bg-light'}`}
                              style={{ width: 'calc(100% - 1rem)' }}
                            >
                              {CATEGORY_CONFIG[cat].icon}
                              <span className="flex-grow-1">{cat}</span>
                              <span style={badgeStyle} className={`badge rounded-pill border fw-normal ${activeCategory === cat ? 'bg-primary text-white border-primary' : 'bg-light text-secondary'}`}>
                                {count}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </nav>


                {/* MAIN CONTENT */}
                <main className="col-md-9 ms-sm-auto col-lg-9 px-md-4 py-4">

                  {/* TOP STATS */}
                  <div className="row g-4 mb-4">
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-2 text-muted">Items Sold ({activeCategory})</h6>
                          <div className="d-flex justify-content-between align-items-end mt-2">
                            <h2 className="card-title fw-bold mb-0">{totalItemsSold}</h2>
                            <div className="bg-success-subtle text-success p-2 rounded">
                              <ShoppingBag size={24} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>


                    <div className="col-md-8">
                      <div className="card border-0 shadow-sm text-white h-100 gradient-card">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <p className="mb-1 text-white text-opacity-75 small fw-bold">KEY INVENTORY IMPACT (Top 3)</p>
                              <h5 className="card-title">Estimated raw materials consumed based on these sales.</h5>
                            </div>
                            <Droplet className="opacity-50" size={32} />
                          </div>
                          <div className="mt-3 d-flex flex-wrap gap-2">
                            {materialUsage.slice(0, 3).map(([name, stats]) => (
                              <span style={badgeStyle} key={name} className="badge glass-badge px-3 py-2- fw-normal">
                                {name}: <span className="fw-bold ms-1">{stats.total % 1 !== 0 ? stats.total.toFixed(1) : stats.total}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>


                  <div className="row g-4">
                    {/* LEFT: TABLE */}
                    <div className="col-lg-8">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                          <h5 className="mb-0 fw-semibold text-dark">
                            {activeCategory === 'All' ? 'Full Sales Report' : `${activeCategory} Breakdown`}
                          </h5>
                          <small className="text-muted fst-italic">* Total Material = Qty × Unit Use</small>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="table-light text-secondary text-uppercase" style={{ fontSize: '0.75rem' }}>
                              <tr>
                                <th className="px-4 py-3 border-0">Item Name</th>
                                <th className="px-4 py-3 border-0 text-center">Qty</th>
                                <th className="px-4 py-3 border-0">Total Material Usage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredData.map((item) => {
                                // Determine badge class based on name
                                let badgeClass = 'bg-light text-dark';
                                let badgeText = '';
                                if (item.name.toLowerCase().includes('full')) { badgeClass = 'bg-primary-subtle text-primary border-primary'; badgeText = 'FULL'; }
                                else if (item.name.toLowerCase().includes('half')) { badgeClass = 'bg-success-subtle text-success border-success'; badgeText = 'HALF'; }
                                else if (item.name.toLowerCase().includes('qtr') || item.name.toLowerCase().includes('quarter')) { badgeClass = 'bg-warning-subtle text-warning border-warning'; badgeText = 'QTR'; }


                                return (
                                  <tr key={item.id}>
                                    <td className="px-4 py-3">
                                      <div className="d-flex align-items-center gap-2">
                                        <span className={`category-dot ${CATEGORY_CONFIG[item.category]?.bsClass.replace('text-', 'bg-').split(' ')[0]}`}></span>
                                        <div>
                                          <span className="fw-medium text-dark d-block">{item.name}</span>
                                          {badgeText && <span style={badgeStyle} className={`badge border ${badgeClass}`} >{badgeText}</span>}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span style={badgeStyle} className="badge bg-light text-dark border">
                                        {item.qty}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="d-flex flex-wrap gap-1">
                                        {Object.entries(item.ingredients).map(([ingName, ingQty]) => (
                                          <span style={badgeStyle} key={ingName} className="badge bg-white text-secondary border fw-normal">
                                            {ingQty * item.qty} {ingName}
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )
                              })}
                              {filteredData.length === 0 && (
                                <tr>
                                  <td colSpan={3} className="text-center py-5 text-muted">
                                    No items found matching your criteria.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>


                    {/* RIGHT: SUMMARY PANEL */}
                    <div className="col-lg-4">
                      <div className="vstack gap-4 position-sticky" style={{ top: '6rem' }}>

                        {/* PORTION ANALYSIS CARD */}
                        {portionStats && (
                          <div className="card border-0 shadow-sm">
                            <div className="card-body p-4">
                              <h5 className="card-title fw-semibold text-dark mb-3 d-flex align-items-center gap-2">
                                <PieChart size={18} className="text-primary" />
                                Portion Analysis
                              </h5>
                              <p className="card-text text-muted small mb-3">
                                Distribution of portion sizes for selected items.
                              </p>

                              <div className="d-flex justify-content-between text-center mb-3">
                                <div className="p-2 rounded bg-primary-subtle border border-primary text-primary w-100 me-2">
                                  <span className="d-block small fw-bold text-uppercase">Full</span>
                                  <span className="h5 mb-0 fw-bold">{portionStats.Full}</span>
                                </div>
                                <div className="p-2 rounded bg-success-subtle border border-success text-success w-100 me-2">
                                  <span className="d-block small fw-bold text-uppercase">Half</span>
                                  <span className="h5 mb-0 fw-bold">{portionStats.Half}</span>
                                </div>
                                <div className="p-2 rounded bg-warning-subtle border border-warning text-warning w-100">
                                  <span className="d-block small fw-bold text-uppercase">Qtr</span>
                                  <span className="h5 mb-0 fw-bold">{portionStats.Quarter}</span>
                                </div>
                              </div>

                              <div className="progress" style={{ height: '8px' }}>
                                <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${(portionStats.Full / (portionStats.Full + portionStats.Half + portionStats.Quarter)) * 100}%` }}></div>
                                <div className="progress-bar bg-success" role="progressbar" style={{ width: `${(portionStats.Half / (portionStats.Full + portionStats.Half + portionStats.Quarter)) * 100}%` }}></div>
                                <div className="progress-bar bg-warning" role="progressbar" style={{ width: `${(portionStats.Quarter / (portionStats.Full + portionStats.Half + portionStats.Quarter)) * 100}%` }}></div>
                              </div>
                            </div>
                          </div>
                        )}


                        {/* TOTAL INVENTORY USED */}
                        <div className="card border-0 shadow-sm">
                          <div className="card-body p-4">
                            <h5 className="card-title fw-semibold text-dark mb-3 d-flex align-items-center gap-2">
                              <ArrowRight size={18} className="text-primary" />
                              Inventory Used ({activeCategory})
                            </h5>
                            <p className="card-text text-muted small mb-4">
                              Aggregated totals for items currently in view.
                            </p>


                            <div className="vstack gap-3">
                              {materialUsage.map(([name, stats]) => (
                                <div key={name} className="d-flex flex-col p-2 rounded hover-bg-light border-bottom border-light">
                                  <div className="d-flex justify-content-between align-items-center w-100 mb-1">
                                    <span className="text-secondary small fw-medium">{name}</span>
                                    <span className="fw-bold text-dark small">
                                      {stats.total % 1 !== 0 ? stats.total.toFixed(1) : stats.total}
                                      <span className="fw-normal text-muted ms-1" style={{ fontSize: '0.7rem' }}>units</span>
                                    </span>
                                  </div>

                                  {/* Breakdown Bar (Only if split exists) */}
                                  {(stats.individual > 0 && stats.combo > 0) && (
                                    <div className="w-100">
                                      <div className="progress" style={{ height: '4px' }}>
                                        <div className="progress-bar bg-secondary" style={{ width: `${(stats.individual / stats.total) * 100}%` }}></div>
                                        <div className="progress-bar bg-warning" style={{ width: `${(stats.combo / stats.total) * 100}%` }}></div>
                                      </div>
                                      <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.65rem' }}>
                                        <span className="text-secondary">Indiv: {stats.individual % 1 !== 0 ? stats.individual.toFixed(1) : stats.individual}</span>
                                        <span className="text-warning-emphasis">Combo: {stats.combo % 1 !== 0 ? stats.combo.toFixed(1) : stats.combo}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {materialUsage.length === 0 && (
                                <div className="text-center py-3 text-muted small">
                                  No materials calculated.
                                </div>
                              )}
                            </div>

                            {
                              permissions && permissions['Export Sales VS Material Report'] &&
                              <div className="mt-4 pt-3 border-top">
                                <button
                                  className="btn btn-dark w-100 shadow-sm fw-medium d-flex align-items-center justify-content-center gap-2"
                                  onClick={handleExport}
                                >
                                  <Download size={18} />
                                  Export Inventory (CSV)
                                </button>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </main>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


export default InventoryDashboard;

const badgeStyle = {
  position: 'static',
  display: "inline-block",
  padding: "0.35em 0.65em 14px 0.65em",
  fontSize: "0.75em",
  fontWeight: 700,
  lineHeight: 1,
  color: "#fff",
  textAlign: "center",
  whiteSpace: "nowrap",
  verticalAlign: "baseline",
  borderRadius: "0.375rem"
};